import { toRaw } from 'vue'
import AuthMixin from '@/components/mixins/auth-mixin'
import EventBus from '@/components/mixins/event-bus-mixin'
import ProjectTemplateMenuComp from './project-template-menu/project-template-menu-tmpl'
import ProjectTemplatePages from './project-template-pages/project-template-pages-tmpl'
import ProjectTemplateControls from './project-template-controls/project-template-controls-tmpl'
import ProjectTemplatePlaybackControls from './project-template-playback-controls/project-template-playback-controls.vue'
import MediaMenuComp from '@/components/common/media-menu/media-menu-tmpl'
import BezelsMask from '@/components/pages/create/bezels-mask/bezels-mask-tmpl'
import UnsavedDataMixin from '@/components/mixins/unsaved-data-mixin'
import OnEnterMixin from '@/components/mixins/onenter-mixin'
import CONST from '@/globals/constants'
import Canvas, { FindPlayerSchedulesByLEDTag } from '@/components/pages/player/player-setup/player-data-canvas'
import { mapActions, mapState as piniaMapState } from 'pinia'
import { scheduleStore } from '@/pinia/stores/schedule/scheduleStore'
import _ from 'lodash'
import { useMixpanel } from '@base-shared/composables/use-mixpanel'

import { isMediaTableEvent, isSelectableMediaEvent, isNewMediaAppEvent } from './project-template-svg'
import { useMediaListStore } from '@/pinia/stores/media/mediaListStore'
import { useMediaStore } from '@/pinia/stores/media/mediaStore'
import { useCognitoAuthStore } from '@base-shared/stores/cognito-auth'

const EDIT_PROJECT_PAGE = 'edit-project-template'
const CREATE_PROJECT_PAGE = 'create-project-template'
const EVENT_ACTIONS = {
  pageSort: 'PAGE_SORT',
  newPageSelected: 'NEW_PAGE_SELECTED',
  deletePage: 'DELETE_PAGE',
  addNewPage: 'ADD_NEW_PAGE',
  pagePropertiesUpdated: 'PAGE_PROPERTIES_UPDATED'
}

export default {
  name: EDIT_PROJECT_PAGE,
  inject: [
    '$currentUser',
    '$utils',
    '$dataStore',
    '$api',
    '$apiError',
    '$filtersRepository',
    '$signedInUserInfo'
  ],
  components: {
    editProjectTemplateMenu: ProjectTemplateMenuComp,
    mediaDetailsMenu: MediaMenuComp,
    ProjectTemplatePages,
    ProjectTemplateControls,
    ProjectTemplatePlaybackControls,
    BezelsMask
  },
  props: {
    routeNameProp: {
      type: String,
      default () { return CREATE_PROJECT_PAGE }
    },
    isActive: {
      type: Boolean,
      default: true
    }
  },
  computed: {
    userSchedulesDoneFetching () {
      return !this.scheduleStoreIsFetching;
    },
    /**
     * Template pages which current user has permission to access
     * @returns an array of pages permitted to the user (not locked by user tags)
     */
    allowedPages () {
      if (this.projectTemplate.data) {
        return this.projectTemplate.data.pages.filter((page) => {
          // The initial page (empty frame) for "custom" template, has no '_edit' key yet, hence should be allowed
          if (!_.has(page, '_edit')) {
            return true
          }

          return !page._edit.lockedByUserTags
        })
      }

      return []
    },
    ...piniaMapState(scheduleStore, {
      scheduleStoreIsFetching: 'isFetching'
    }),
    /**
     * returns true only if template frame status is NOT PLAY FRAME
     * AND template is neither an alert nor an overlay
     */
    showTopAddAppsOverlayIcon () {
      const isDiscoveryAccount = this.$signedInUserInfo?._edit.plan.currentPlan === this.$utils.options.plans.DISCOVERY

      // On initial render, while projectTemplate is still empty, by default return false to avoid sort of a glitch
      // Hide overlay on discovery accounts
      if (_.isEmpty(this.projectTemplate) || isDiscoveryAccount) return false

      const isFrameStatusPlayFrame = this.projectTemplateFrameStatus === this.projectTemplateFrameStatusOptions.PLAY_FRAME
      const isTemplateAlert = this.projectTemplate.alert
      const isTemplateOverlay = this.projectTemplate.data?.skin

      return !(isFrameStatusPlayFrame || isTemplateAlert || isTemplateOverlay)
    },
    /**
     * Looks for the overlay app from the list of all apps available
     * for the user.
     * In SVG Player edit/index.js, overlay app is included in the list IF user is a
     * customEditUser AND the project does not have an existing overlay yet.
     * @returns an object containing overlay-related data
     */
    getOverlayFromApps () {
      const findOverlay = this.addAppsTabMenuItems.find((app) => { return app.value === 'overlay' && app.overlay })

      return findOverlay
    }
  },
  watch: {
    scheduleStoreIsFetching (newVal) {
      this.$utils.mainLoader(newVal)
    },
    routeNameProp (newVal) {
      this.routeName = newVal
      this.setDefaultData()
    },
    projectTemplateFrameStatus (newVal) {
      this.handleSubmitButtonText(newVal)
      this.projectTemplate = this.filterProjectTemplateObject(this.projectTemplate)
    },
    svgActivateClicks (newVal) {
      // When SVG has a feedback that clicks is activated then send the project data to SVG
      if (newVal.isActivated && this.projectTemplateFrameStatus === this.projectTemplateFrameStatusOptions.EDIT_FRAME) {
        const projectTemplate = this.$utils.clone(this.projectTemplate)
        projectTemplate.data.dir = this.$filtersRepository.findOrientation() ?? CONST.ORIENTATION_VALUE_HORIZONTAL
        this.handleProjectPreview(projectTemplate)
      }
    },
    isIframeDataChanged (changed) {
      // Watch changes of isIframeDataChanged, if it becomes true (triggered by events that make changes to the iframe), set unsaved data flag to local storage (which will be used for redirect checks). Otherwise, just remove
      if (changed) {
        this.setUnsavedDataFlagIntoLocalstorage({ page: 'projectTemplate' })
      } else {
        this.removeUnsavedDataFlagInLocalStorage()
      }
    }
  },
  mixins: [AuthMixin, UnsavedDataMixin, OnEnterMixin],
  data () {
    return {
      CONST,
      enableScheduleSetTarget: this.$currentUser.getRights().userSchedule.setTarget,
      error: '',
      currentScreenIndex: 0, // Current Page Number or called as currentScreenIndex
      projectTemplate: {},
      openMediaPickerDialogData: {
        menuItemId: '',
        time: null
      },
      projectTemplateActionOptions: { CREATE_PROJECT_TEMPLATE: 'CREATE_PROJECT_TEMPLATE', EDIT_PROJECT_TEMPLATE: 'EDIT_PROJECT_TEMPLATE' },
      projectTemplateAction: 'CREATE_PROJECT_TEMPLATE',
      projectTemplateFrameStatusOptions: { EDIT_FRAME: 'EDIT_FRAME', PLAY_FRAME: 'PLAY_FRAME' },
      projectTemplateFrameStatus: 'EDIT_FRAME',
      projectMediaDetails: null,
      projectOrientation: 'horizontal', // options: horizontal and VERTICAL
      editProjectName: {
        value: '',
        editing: false
      },
      selectedEditFrameTab: null,
      routeName: this.routeNameProp,
      userSchedules: [],
      userSchedulesFilteredByResolution: [],
      submitButtonText: 'NEXT',
      menuItemInProgressRequests: [],
      menuItemTabIds: { addApps: 'addApps', settings: 'settings' },
      addAppsMenuItemsTab: [],
      addAppsTabIconVisible: false,
      addAppsTabMenuItems: [],
      svgActivateClicks: { isActivated: false, ref: null },
      tagsSummaryForSuggestions: [],
      customResolution: {
        width: 0,
        height: 0,
        custom: false,
        ui: { width: null, height: null, aspectRatio: null, elStyle: {} }
      },
      showMediaOrientationIcon: false,
      addDefaultTags: [],
      projectTemplatePagesIFramePreview: null,
      isIframeDataChanged: false,
      isOverviewVisible: false,
      isAlignElementVisible: false,
      playbackControlsMessageQueue: [],
      bezelsPreviewInfo: {
        bezels: [],
        width: null,
        height: null
      }
    }
  },
  setup () {
    const mediaStore = useMediaStore()
    const mediaListStore = useMediaListStore()

    return {
      // Expose the media pinia stores in Options API
      mediaStore,
      mediaListStore
    };
  },
  methods: {
    ...mapActions(scheduleStore, ['fetchAllSchedules']),
    /**
     * Set Project Template Action and Frame status
     */
    async setDefaultData () {
      const routeName = this.routeName
      const routeParams = this.$route.params

      // Reset Current Frame Page Number
      this.resetCurrentScreenIndex()
      const createNormalProjectPage = routeName === CREATE_PROJECT_PAGE

      // 'new' is only the path for custom template.Originally 'custom' is the name for empty template.
      const createCustomProjectPage = routeParams.pageContent === 'new'
      const editProjectPage = routeName === EDIT_PROJECT_PAGE

      // If this Component use for Create Project Page
      if (createNormalProjectPage || createCustomProjectPage) {
        this.projectTemplateAction = this.projectTemplateActionOptions.CREATE_PROJECT_TEMPLATE

        // Set Project Template Orientation
        this.projectOrientation = this.$filtersRepository.findOrientation() ?? CONST.ORIENTATION_VALUE_HORIZONTAL

        const setId = (createCustomProjectPage) ? 'default' : routeParams.setId
        let templateId = 'custom-0001'

        if (!createCustomProjectPage) {
          // If creating new project from videowall page, use led template
          templateId = routeParams.templateId === 'videowall' ? 'led' : routeParams.templateId
        }

        // Get Project Details
        const nextAction = { callback: () => { this.getTagsSummary({ closeLoader: true }) } }
        this.getProjectTemplate('newProject', { setId, templateId, templateSchedule: 'templateSchedule' }, nextAction)
      }

      // If this Component use for Edit Project Page
      if (editProjectPage) {
        this.projectTemplateAction = this.projectTemplateActionOptions.EDIT_PROJECT_TEMPLATE

        // Set Project Frame Status
        if (routeParams.projectFrameAction && (routeParams.projectFrameAction === 'edit-frame')) {
          this.projectTemplateFrameStatus = this.projectTemplateFrameStatusOptions.EDIT_FRAME
        } else {
          this.projectTemplateFrameStatus = this.projectTemplateFrameStatusOptions.PLAY_FRAME
        }

        // Get Project Template Details and Project Media Details
        const nextAction = {
          callback: () => {
            this.getProjectMediaDetails(this.$route.params.projectId)
            this.getTagsSummary()
          }
        }
        this.getProjectTemplate('existingProject', { projectId: this.$route.params.projectId }, nextAction)
        // Remove unsaved data flag
        this.isIframeDataChanged = false
      }

      // Set Project Template Orientation
      this.projectOrientation = this.$filtersRepository.findOrientation() ?? CONST.ORIENTATION_VALUE_HORIZONTAL

      if (this.$route.query.videowallTags) {
        const { width, height } = this.$utils.parseLedTag(`LED:${this.$route.query.videowallTags}`)

        const canvasInstance = new Canvas(this.$api)
        const { ok: existingCanvas } = await canvasInstance.findExistingCanvas('videowall', `${width} ${height}`)

        if (existingCanvas) {
          this.bezelsPreviewInfo = {
            bezels: existingCanvas.bezels,
            width: Number(width),
            height: Number(height)
          }
        }
      }

      // Auto Adjust component style
      this.$nextTick(_ => { this.$utils.updatePageComponentStyle({ name: 'createEditProjectPage' }) })
    },
    setCustomResolution (projectTemplate) {
      this.showMediaOrientationIcon = !this.$currentUser.hasGroupUserLimited()

      const calculateAspectRatioFitParams = {
        srcWidth: 0,
        srcHeight: 0,
        maxWidth: $('.project-frame-content').width(),
        maxHeight: $('.project-frame-content').height()
      }

      // Set srcWidth and srcHeight params to calculte ratio for UI
      if (this.projectTemplate && this.projectTemplate.led) {
        const splitLEDVal = this.projectTemplate.led.split('x')
        calculateAspectRatioFitParams.srcWidth = splitLEDVal[0]
        calculateAspectRatioFitParams.srcHeight = splitLEDVal[1]
      } else if (this.projectTemplate.data && (this.projectTemplate.data.width && this.projectTemplate.data.height)) {
        calculateAspectRatioFitParams.srcWidth = projectTemplate.data.width
        calculateAspectRatioFitParams.srcHeight = projectTemplate.data.height
      }

      // Set customResolution Data
      if (calculateAspectRatioFitParams.srcWidth && calculateAspectRatioFitParams.srcHeight) {
        const calculateSizeForUI = this.$utils.calculateAspectRatioFit(calculateAspectRatioFitParams)
        this.customResolution = {
          width: projectTemplate.data.width,
          height: projectTemplate.data.height,
          custom: true,
          ui: {
            width: calculateSizeForUI.cardWidth,
            height: calculateSizeForUI.cardHeight,
            aspectRatio: calculateSizeForUI.aspectRatio,
            elStyle: { width: `${calculateSizeForUI.cardWidth}px`, height: `${calculateSizeForUI.cardHeight}px` }
          }
        }
        this.showMediaOrientationIcon = false
      }
    },
    resetCustomResolution () {
      this.customResolution = {
        width: 0,
        height: 0,
        custom: false,
        ui: {
          width: null,
          height: null,
          aspectRatio: null,
          elStyle: {}
        }
      }
      this.showMediaOrientationIcon = !this.$currentUser.hasGroupUserLimited()
    },
    /**
     * This will get Project Template details from API
     * @param {String} reqType
     * @param {Object} params
     * @param {Object} nextAction - a process once the request is done
     */
    getProjectTemplate (reqType, params, nextAction) {
      let endpoint = ''
      const queryParams = {
        locale: this.$t?.locale?.value ?? 'en',
        width: 1280,
        use_qsa: 1
      }
      let templateDimensions = null

      // Set Default Tags and template dimensions from query url
      if (params.templateId && params.templateId === 'led' && this.$route.query) {
        if (this.$route.query.dimensions) {
          templateDimensions = this.$route.query.dimensions
        }
      }

      // Add Default tags for user with tags
      if (reqType === 'newProject' && this.$currentUser.getUserTags().length) {
        this.addDefaultTags = this.$currentUser.getUserTags()
      }

      if (reqType === 'newProject') {
        endpoint = `${CONFIG.API_BASE_URL}/template/${params.setId}/${params.templateId}`
      } else if (this.projectTemplateAction === this.projectTemplateActionOptions.EDIT_PROJECT_TEMPLATE) {
        endpoint = `${CONFIG.API_BASE_URL}/project/${params.projectId}`
      }

      if (templateDimensions) {
        queryParams.dimensions = templateDimensions
      }

      const apiRequest = () => {
        this.$utils.mainLoader(true)
        this.$api.get(endpoint, { params: queryParams })
          .then(response => {
            this.projectTemplate = this.filterProjectTemplateObject(response.data)
            this.editProjectName.value = this.projectTemplate.name

            if (reqType === 'newProject') {
              if (this.$route.query.videowallTags) {
                this.editProjectName.value = 'New Videowall Project'
                this.applyNewProjectTemplateName()
              } else if (this.$route.query.ledTags) {
                this.editProjectName.value = 'New LED Project'
                this.applyNewProjectTemplateName()
              }
            }

            // If there are page restrictions, get the first page index from allowedPages array. Otherwise, set to a negative number so the page won't be displayed on the frame.
            if (this.allowedPages.length) {
              const firstPage = this.allowedPages[0]
              this.currentScreenIndex = this.projectTemplate.data.pages.findIndex((page) => page.uid === firstPage.uid)
            } else {
              this.currentScreenIndex = -1
            }

            this.setCustomResolution(this.projectTemplate)

            useMixpanel().track('Project', {
              templateId: this.projectTemplate.templateId,
              action: this.projectTemplateAction
            })

            if (typeof (nextAction) === 'object') {
              nextAction.callback()
            } else {
              this.$utils.mainLoader(false)
            }
          })
          .catch((error) => {
            this.error = error.message
            this.$utils.mainLoader(false)
            this.$apiError.handleAPIRequestError(error, { originalRequest: () => { apiRequest() }, showDialogNotification: true })
          })
      }
      apiRequest()
    },
    /**
     * This will get project media details from API
     * @param {String} resourceId - project resourceId
     */
    getProjectMediaDetails (resourceId, triggerMediaMenuPropertyChange) {
      const apiRequest = () => {
        this.$utils.mainLoader(true)
        const params = {
          use_qsa: 1
        }

        this.projectMediaDetails = null
        this.$api.get(`${CONFIG.API_BASE_URL}/media/${resourceId}`, { params })
          .then((response) => {
            this.projectMediaDetails = response.data

            // Make sure to get back schedule data changes after switching tabs
            if (triggerMediaMenuPropertyChange) {
              this.$nextTick(_ => {
                if (this.$refs.projectDetailsMenu) {
                  this.$refs.projectDetailsMenu.handlePropertyChange('schedules')
                }
              })
            }
            this.getUserSchedules();
          })
      }
      apiRequest()
    },

    async getUserSchedules () {
      const { success, data: schedules } = await this.fetchAllSchedules(this.projectMediaDetails.alert);

      if (!success) {
        EventBus.$emit(
          'SHOW_CUSTOM_NOTIFICATION',
          {
            type: 'error',
            alert: {
              message: this.$t('errors.unexpectedError')
            },
            closeButtonText: this.$t('buttons.reload'),
            callbacks: {
              eventOnClose: () => window.location.reload()
            }
          }
        );

        return;
      }
      this.userSchedules = schedules
      this.userSchedulesFilteredByResolution = this.getSchedulesByResolution(_.clone(schedules));
    },
    /**
     * This will get project media details from API
     * @param {String} resourceId - project resourceId
     */
    getTagsSummary (params) {
      if (!params) params = {}

      const apiRequest = () => {
        let endPoint = `${CONFIG.API_BASE_URL}`
        endPoint += '/group/tags/?type=resourceTags,playerTags,styleTags&ext=1'
        this.$api.get(`${endPoint}`)
          .then(response => {
            const responseData = response.data
            const tagsSummary = []

            // Adjust tags for summary
            if (this.projectTemplate.alert) {
              if (responseData.resourceTags) delete responseData.resourceTags
            }

            if (!this.projectTemplate.alert) {
              if (responseData.alertTags) delete responseData.alertTags
            }

            for (const tagType in responseData) {
              for (const tag of responseData[tagType]) {
                if (tagsSummary.indexOf(tag) < 0) tagsSummary.push(tag)
              }
            }

            this.tagsSummaryForSuggestions = this.$utils.filterTagOptionsForUI({ tags: tagsSummary })

            if (params.closeLoader) this.$utils.mainLoader(false)
          })
          .catch((error) => {
            this.$apiError.handleUnauthorizedError(error, { originalRequest: () => { apiRequest() } })
          })
      }
      apiRequest()
    },
    /**
     * This will customize Project Template Object
     * @param {Object} projectTemplateSrc - original object source
     */
    filterProjectTemplateObject (projectTemplateSrc) {
      const projectTemplate = this.$utils.clone(projectTemplateSrc)

      if (!projectTemplate._edit) {
        projectTemplate._edit = {}
      }

      projectTemplate._edit.iFrameStatus = this.projectTemplateFrameStatus
      projectTemplate._edit.uid = projectTemplate.templateId

      // Add uid in data pages
      if (projectTemplate.data && projectTemplate.data.pages) {
        for (const pageIndx in projectTemplate.data.pages) {
          if (!projectTemplate.data.pages[pageIndx].uid) {
            projectTemplate.data.pages[pageIndx].uid = `${this.$utils.generateUniqueID()}-${pageIndx}`
          }
        }
      }

      // Set Project Type as Multiple Page all the time
      projectTemplate._edit.projectType = 'multiplePage'

      // Set setOptions
      projectTemplate._edit.setOptions = this.$utils.clone(projectTemplate.setOptions)

      // Create editable project data from data pages
      projectTemplate._edit.data = {
        style: [],
        pages: projectTemplate.data && projectTemplate.data.pages ? projectTemplate.data.pages : [],
        dataSourceKey: 'pages'
      }

      // Create Project Properties or called as Menu Items
      if (!projectTemplate._edit.menu) { // This condition will prevent reseting of values in the next cycle event
        projectTemplate._edit.menu = {
          menuItemsSourceKey: 'pagesItems',
          styleItems: (projectTemplate.style && projectTemplate.style.items) ? projectTemplate.style.items : [],
          pagesItems: (projectTemplate.pages && projectTemplate.pages.items) ? projectTemplate.pages.items : []
        }
      }

      if (projectTemplate.ui) {
        // need to update the UI and force teh pages mode
        projectTemplate._edit.menu.pagesItems = projectTemplate.ui
        projectTemplate.pages.items = projectTemplate.ui
        delete projectTemplate.ui
      }

      // Set Set Options Style Status
      projectTemplate._edit.setOptionsStyleStatus = this.$utils.clone(projectTemplate.style)
      if (projectTemplate._edit.setOptionsStyleStatus && projectTemplate._edit.setOptionsStyleStatus.items) delete projectTemplate._edit.setOptionsStyleStatus.items

      /* Filter Menu Items because there are items with sub properties. It means that the user can Add
        or Remove an element/item from given properties.
      */
      for (const menuItem of projectTemplate._edit.menu[projectTemplate._edit.menu.menuItemsSourceKey]) {
        // Make sure that the menuItem has an 'array' property
        if (Object.prototype.hasOwnProperty.call(menuItem, 'array') && menuItem.array === true) {
          if (!menuItem._arrayProperties) { // This condition will prevent reseting of values in the next cycle event
            menuItem._arrayProperties = {
              labelSource: '',
              properties: [],
              selectedPropertyKey: 0
            }
          }

          /* Get each propertyies and check if there's a primary flag. Properties with primary flag will be set
           as label source, by befault the first property is set.
          */
          if ((menuItem.properties) && (menuItem.properties.length > 0)) {
            menuItem._arrayProperties.labelSource = menuItem.properties[0].name // Default labelSource

            for (const sIPropIndx in menuItem.properties) {
              if (menuItem.properties[sIPropIndx].primary) {
                menuItem._arrayProperties.labelSource = menuItem.properties[sIPropIndx].name
              }
            }

            menuItem._arrayProperties.properties = menuItem.properties
          }
        }
      }

      // Grouped Menu Items
      projectTemplate._edit.menu[projectTemplate._edit.menu.menuItemsSourceKey].forEach((menuItem, indx) => {
        if (menuItem.properties) {
          if (menuItem.id.includes('pricelist') || menuItem.id.includes('times') || menuItem.id.includes('openinghours') || menuItem.id.includes('wayfinding')) {
            // Format menu items for MENU App
            menuItem.label = this.$t('template.tableSettings')

            if (menuItem.selectGroup) {
              if (menuItem.selectGroup === 'angle') {
                menuItem.label = this.$t('template.arrowSettings')
              } else {
                menuItem.label = this.$t('template.cellSettings')
              }
            }

            // For Cell Settings, add displayAlign and textAlign properties
            if (menuItem.selectGroup) {
              projectTemplate._edit.menu[projectTemplate._edit.menu.menuItemsSourceKey][indx - 1].isHidden = true
            }
          } else if (['box', 'textbox'].includes(menuItem.style)) {
            // Format menu items for the SHAPE App
            menuItem.label = this.$t('template.shapeSettings')
          } else if (['text'].includes(menuItem.style)) {
            // Format menu items for TEXT App
            menuItem.label = this.$t('template.textSettings')
          } else if (menuItem.id.includes('rss')) {
            menuItem.label = this.$t('template.rssSettings')

            const urlProp = menuItem.properties.find((prop) => prop.name === 'url' && prop.type === 'choice')

            if (urlProp) {
              const selectedUrl = urlProp?.values?.find((item) => item.value === projectTemplate._edit.data.pages[this.currentScreenIndex].rss.url)

              menuItem.selectedLang = selectedUrl ? selectedUrl.lang : projectTemplate._edit.data.pages[this.currentScreenIndex].rss.urlLang
            }
          } else if (menuItem.id.includes('city')) {
            const locationProps = projectTemplate._edit.menu[projectTemplate._edit.menu.menuItemsSourceKey][indx - 1]
            // Format menu items for WEATHER App
            menuItem.label = this.$t('template.weatherLocationSettings')
            menuItem.locationId = locationProps.id

            // Merge the ui data into 1, combining the properties
            if (!menuItem.properties.find((prop) => prop.type === 'cityid')) {
              projectTemplate._edit.menu[projectTemplate._edit.menu.menuItemsSourceKey][indx - 1].isHidden = true
              menuItem.properties = [...locationProps.properties, ...menuItem.properties]
            }
          } else if (menuItem.id.includes('forecast')) {
            const displayProps = projectTemplate._edit.menu[projectTemplate._edit.menu.menuItemsSourceKey][indx - 1]
            menuItem.label = this.$t('template.weatherIconSettings')
            menuItem.displayId = displayProps.id

            // Merge the ui data into 1, combining the properties
            if (!menuItem.properties.find((prop) => prop.name === 'unit')) {
              projectTemplate._edit.menu[projectTemplate._edit.menu.menuItemsSourceKey][indx - 1].isHidden = true
              menuItem.properties = [...displayProps.properties, ...menuItem.properties]
            }
          } else if (menuItem.style === 'timebox') {
            menuItem.label = this.$t('template.clockSettings')
          } else if (menuItem.style === 'countdown') {
            menuItem.label = this.$t('template.countdownSettings')
          }

          // Color Groups
          const createColorGroupItems = menuItem.properties.findIndex((property) => { return (typeof property === 'object' && property !== null) && (property.name === 'uiGroupedMenuItemsColor') }) < 0
          if (createColorGroupItems) {
            const newGroupedItems = {
              type: 'uiGroupedMenuItems',
              uiGroupId: this.$utils.generateUniqueID(),
              name: 'uiGroupedMenuItemsColor',
              properties: [],
              extended: false
            }
            let keepFirstColorType = null

            menuItem.properties.forEach((property, indx) => {
              if ((typeof property === 'object' && property !== null) && (property.type === 'color')) {
                newGroupedItems.properties.push(menuItem.properties[indx])
                menuItem.properties[indx].uiGroupId = newGroupedItems.uiGroupId

                if (keepFirstColorType === null) {
                  keepFirstColorType = { indx, property }
                }
              }
            })

            if (newGroupedItems.properties.length) {
              menuItem.properties.push(newGroupedItems)
              // Interchange the position from the first color type
              menuItem.properties[menuItem.properties.length - 1] = keepFirstColorType.property
              menuItem.properties[keepFirstColorType.indx] = newGroupedItems
            }
          }

          // Create Date and Time Group
          const createDateAndTimeGroupItems = menuItem.properties.findIndex((property) => { return (typeof property === 'object' && property !== null) && (property.name === 'uiGroupedMenuItemsDateAndTime') }) < 0
          const dateIndx = menuItem.properties.findIndex((property) => { return (typeof property === 'object' && property !== null) && (property.name === 'date') })
          const timeIndx = menuItem.properties.findIndex((property) => { return (typeof property === 'object' && property !== null) && (property.name === 'time') })
          if (createDateAndTimeGroupItems && (dateIndx >= 0 && timeIndx >= 0)) {
            const newGroupedItems = {
              type: 'uiGroupedMenuItems',
              uiGroupId: this.$utils.generateUniqueID(),
              name: 'uiGroupedMenuItemsDateAndTime',
              properties: []
            }

            newGroupedItems.properties.push(menuItem.properties[dateIndx])
            newGroupedItems.properties.push(menuItem.properties[timeIndx])
            menuItem.properties[dateIndx].uiGroupId = newGroupedItems.uiGroupId
            menuItem.properties[timeIndx].uiGroupId = newGroupedItems.uiGroupId

            if (newGroupedItems.properties.length) {
              // Swap index value to change the order of the group items
              menuItem.properties[menuItem.properties.length - 1] = menuItem.properties[dateIndx]
              menuItem.properties[dateIndx] = newGroupedItems
            }
          }

          // Create Text Format Group
          const createTextFormatGroupItems = menuItem.properties.findIndex((property) => { return (typeof property === 'object' && property !== null) && (property.name === 'uiGroupedMenuItemsTextFormat') }) < 0
          if (createTextFormatGroupItems) {
            const newGroupedItems = {
              type: 'uiGroupedMenuItems',
              uiGroupId: this.$utils.generateUniqueID(),
              name: 'uiGroupedMenuItemsTextFormat',
              properties: [],
              hasFontSizeEl: false
            }
            const fontFamilyIndx = menuItem.properties.findIndex((property) => { return (typeof property === 'object' && property !== null) && (property.name === 'fontFamily') })
            const fontWeightIndx = menuItem.properties.findIndex((property) => { return (typeof property === 'object' && property !== null) && (property.name === 'fontWeight') })
            const fontSizeIndx = menuItem.properties.findIndex((property) => { return (typeof property === 'object' && property !== null) && (property.name === 'fontSize') })
            const fontBoldIndx = menuItem.properties.findIndex((property) => { return (typeof property === 'object' && property !== null) && (property.name === 'fontBold') })
            const fontStyleIndx = menuItem.properties.findIndex((property) => { return (typeof property === 'object' && property !== null) && (property.name === 'fontStyle') })

            if (fontFamilyIndx >= 0) {
              newGroupedItems.properties.push(menuItem.properties[fontFamilyIndx])
              menuItem.properties[fontFamilyIndx].uiGroupId = newGroupedItems.uiGroupId
            }

            if (fontWeightIndx >= 0) {
              newGroupedItems.properties.push(menuItem.properties[fontWeightIndx])
              menuItem.properties[fontWeightIndx].uiGroupId = newGroupedItems.uiGroupId
            }

            if (fontBoldIndx >= 0) {
              newGroupedItems.properties.push(menuItem.properties[fontBoldIndx])
              menuItem.properties[fontBoldIndx].uiGroupId = newGroupedItems.uiGroupId
            }

            if (fontStyleIndx >= 0) {
              newGroupedItems.properties.push(menuItem.properties[fontStyleIndx])
              menuItem.properties[fontStyleIndx].uiGroupId = newGroupedItems.uiGroupId
            }

            if (fontSizeIndx >= 0) {
              newGroupedItems.hasFontSizeEl = true
              newGroupedItems.properties.push(menuItem.properties[fontSizeIndx])
              menuItem.properties[fontSizeIndx].uiGroupId = newGroupedItems.uiGroupId
            }

            if (newGroupedItems.properties.length) {
              menuItem.properties.push(newGroupedItems)
              // Swap index value to change the order of the group items
              menuItem.properties[menuItem.properties.length - 1] = menuItem.properties[fontWeightIndx]
              menuItem.properties[fontWeightIndx] = newGroupedItems
            }
          }

          // Create Text Alignment Group
          const createTextAlignGroupItems = menuItem.properties.findIndex((property) => { return (typeof property === 'object' && property !== null) && (property.name === 'uiGroupedMenuItemsTextAlign') }) < 0
          if (createTextAlignGroupItems) {
            const newGroupedItems = {
              type: 'uiGroupedMenuItems',
              uiGroupId: this.$utils.generateUniqueID(),
              name: 'uiGroupedMenuItemsTextAlign',
              properties: [],
              extended: false
            }
            const textAlignIndx = menuItem.properties.findIndex((property) => { return (typeof property === 'object' && property !== null) && (property.name === 'textAlign') })
            const displayAlignIndx = menuItem.properties.findIndex((property) => { return (typeof property === 'object' && property !== null) && (property.name === 'displayAlign') })

            if (textAlignIndx >= 0 && displayAlignIndx >= 0) {
              newGroupedItems.properties.push(menuItem.properties[textAlignIndx])
              newGroupedItems.properties.push(menuItem.properties[displayAlignIndx])
              menuItem.properties[textAlignIndx].uiGroupId = newGroupedItems.uiGroupId
              menuItem.properties[displayAlignIndx].uiGroupId = newGroupedItems.uiGroupId

              if (menuItem.properties[textAlignIndx].extended) {
                newGroupedItems.extended = true
              }
            }

            if (newGroupedItems.properties.length) {
              menuItem.properties.push(newGroupedItems)
            }
          }
        }
      })

      // Add schedule tags. A collection from data pages tags
      if (!projectTemplate.tags) projectTemplate.tags = []
      if (projectTemplate.data && projectTemplate.data.pages) {
        const projectTags = []

        projectTemplate.data.pages.forEach((pageProp, pageIndx) => {
          // Add Default tags from queryURL
          if (this.addDefaultTags.length) {
            if (!pageProp.tags) projectTemplate.data.pages[pageIndx].tags = []

            this.addDefaultTags.forEach((dTag) => {
              if (projectTemplate.data.pages[pageIndx].tags.indexOf(dTag) < 0) {
                projectTemplate.data.pages[pageIndx].tags.push(dTag)
              }
            })
          }

          if (pageProp.tags) {
            const pageTags = pageProp.tags

            for (const indx in pageTags) {
              const tag = pageTags[indx]
              const existInProjectTags = projectTags.indexOf(tag) >= 0

              if (!existInProjectTags) {
                projectTags.push(tag)
              }
            }
          }
        })

        projectTemplate.tags = projectTags
      }

      // Validate data pages
      if ((projectTemplate.data && projectTemplate.data.pages) && this.projectTemplateAction === this.projectTemplateActionOptions.EDIT_PROJECT_TEMPLATE) {
        for (const pageIndx in projectTemplate.data.pages) {
          const pageItem = projectTemplate.data.pages[pageIndx]

          pageItem._edit = pageItem?._edit ?? {}
          pageItem._edit.uid = pageItem?.uid ?? `${this.$utils.generateUniqueID()}-${pageIndx}`
          pageItem._edit.lockedByUserTags = false

          // Lock pages for user with customTags and not the owner of the template
          if (this.$currentUser.getUserTags().length && (this.$currentUser.getUserId() !== projectTemplate.userId)) {
            if (_.isArray(pageItem.tags) && pageItem.tags.length) {
              pageItem._edit.lockedByUserTags = !pageItem.tags.some(item => this.$currentUser.getUserTags().includes(item))
            } else if (_.isArray(pageItem.tags) && !pageItem.tags.length) {
              pageItem._edit.lockedByUserTags = true
            }
          }
        }
      }

      return projectTemplate
    },
    getSchedulesByResolution (schedules = []) {
      const isLEDProject = this.projectTemplate.templateId === 'led' || this.projectTemplate.led

      if (!isLEDProject) {
        return []
      }

      if (isLEDProject && !_.isEmpty(this.$route.query.videowallTags)) {
        return FindPlayerSchedulesByLEDTag(`LED:${this.$route.query.videowallTags}`, schedules)
      }

      if (isLEDProject) {
        return FindPlayerSchedulesByLEDTag(`LED:${this.$route.query.ledTags}`, schedules)
      }
    },
    resetCurrentScreenIndex () {
      this.currentScreenIndex = 0
    },
    handleProjectPageChange (event) {
      const { newPageSelected, ...rest } = EVENT_ACTIONS
      const CHANGE_TRIGGERING_EVENTS = Object.values(rest)

      if (this.currentScreenIndex < 0) {
        return
      }

      if (CHANGE_TRIGGERING_EVENTS.includes(event.action)) {
        this.isIframeDataChanged = true
      }

      if (event.action === EVENT_ACTIONS.pageSort) {
        const projectTemplate = this.$utils.clone(this.projectTemplate)
        projectTemplate.data.pages = event.payload.data.pages
        this.currentScreenIndex = event.payload.data.newScreenIndex
        this.projectTemplate = this.filterProjectTemplateObject(projectTemplate)
        this.handleProjectPreview(this.projectTemplate)
      }

      if (event.action === EVENT_ACTIONS.newPageSelected) {
        this.currentScreenIndex = event.payload.data.pageNumber
        // Send updated projectTemplate data to SVG first
        this.handleProjectPreview(this.projectTemplate)
        // Then clear selected target
        this.postMessageToSVG({ selectedTargetId: null })
      }

      if (event.action === EVENT_ACTIONS.deletePage) {
        const pageNumberToDelete = event.payload.data.pageNumber
        const projectTemplate = this.$utils.clone(this.projectTemplate)

        projectTemplate.data.pages.splice(pageNumberToDelete, 1)
        this.currentScreenIndex = event.payload.data.newScreenIndex

        this.projectTemplate = this.filterProjectTemplateObject(projectTemplate)
        this.handleProjectPreview(projectTemplate)
      }

      if (event.action === EVENT_ACTIONS.addNewPage) {
        const projectTemplate = this.$utils.clone(this.projectTemplate)
        let newPage

        const emptyPage = event.payload.data?.emptyPage // empty page
        if (emptyPage) {
          newPage = {
            settings: {},
            tags: [],
            addApps: this.$utils.clone(projectTemplate.data.pages[this.currentScreenIndex].addApps),
            apps: []
          }
        } else {
          newPage = this.$utils.clone(projectTemplate.data.pages[this.currentScreenIndex])
        }

        /* empty page for overlay */
        if (newPage && newPage.settings && newPage.settings.overlay) {
          newPage = {
            settings: {},
            tags: [],
            addApps: this.$utils.clone(projectTemplate.data.pages[this.currentScreenIndex].addApps)
          }
        }

        // If current user has tags then merge it
        if (this.$currentUser.getUserTags().length) {
          this.$currentUser.getUserTags().forEach((userTag) => {
            if (newPage.tags.indexOf(userTag) < 0) {
              newPage.tags.push(userTag)
            }
          })
        }

        // Add new page into project data then set the new screen index
        newPage.uid = `${this.$utils.generateUniqueID()}-${projectTemplate.data.pages.length + 1}`
        projectTemplate.data.pages.push(newPage)
        this.projectTemplate = this.filterProjectTemplateObject(projectTemplate)
        this.currentScreenIndex = (projectTemplate.data.pages.length - 1)
        // Call preview to send new page data to SVG
        this.handleProjectPreview(this.projectTemplate)
        // Send message to SVG to clear selected target (clear selection from last selected page) to update context menu (ui message we get from svg)
        this.postMessageToSVG({ selectedTargetId: null })
      }

      if (event.action === EVENT_ACTIONS.pagePropertiesUpdated) {
        const projectTemplate = this.$utils.clone(this.projectTemplate)
        projectTemplate.data.pages = event.payload.data.pages
        this.projectTemplate = this.filterProjectTemplateObject(projectTemplate)
        this.handleProjectPreview(this.projectTemplate)
      }
    },
    /**
     * Hook function when the project properties sub-component change or throw an event
     * @param {event} event - event details
     */
    handleEditProjectTemplateChange (event) {
      let projectTemplate = this.$utils.clone(this.projectTemplate)
      switch (event.action) {
        case 'PROJECT_TEMPLATE_PREVIEW': // Preview Project Template
          this.handleProjectPreview(event.payload.data)
          break
        case 'PROJECT_TEMPLATE_APPLY_DATA_CHANGES': // Update Project Template original object
          // Event for when background has been changed
          projectTemplate = event.payload.data

          this.projectTemplate = this.filterProjectTemplateObject(projectTemplate)
          this.isIframeDataChanged = true
          break
        case 'RESET_DATA_CHANGES':
          this.projectTemplate = this.filterProjectTemplateObject(this.projectTemplate)
          this.handleProjectPreview(this.projectTemplate)
          break
        case 'MENU_ITEM_REQUEST_START':
          if (event.payload.data.name) {
            this.menuItemInProgressRequests.push(event.payload.data.name)
          }
          break
        case 'MENU_ITEM_REQUEST_DONE':
          if (event.payload.data.name) {
            this.menuItemInProgressRequests = this.menuItemInProgressRequests.filter((item) => {
              return item !== event.payload.data.name
            })
          }
          break
        case 'ALIGN_ELEMENTS':
          this.postMessageToSVG({
            command: 'align',
            meta: {
              xAlign: event.payload?.data?.xAlign,
              yAlign: event.payload?.data?.yAlign
            }
          })
          break
      }
    },
    /**
     * Hook function if media menu/properties sub-component will change or throw an action
     * @param {Object} event - event deatils
     */
    menuMediaDetailsOnChange (event) {
      switch (event.action) {
        case this.projectTemplateActionOptions.EDIT_PROJECT_TEMPLATE:
          this.projectTemplateFrameStatus = this.projectTemplateFrameStatusOptions.EDIT_FRAME
          this.projectTemplateAction = this.projectTemplateActionOptions.EDIT_PROJECT_TEMPLATE
          break
        case 'UPDATE_PROJECT_TEMPLATE_MEDIA_DATA':
          this.projectMediaDetails = event.payload.data
          break
      }
    },
    /**
    * Handle Project Preview by sending a post message into the IFrame
    * @param {Object} data - data to submit into iFrame
    */
    handleProjectPreview (data) {
      const postMessageDataModel = {
        setOptions: {},
        projectOptions: {}
      }

      postMessageDataModel.projectOptions = {
        ...toRaw(data.data),
        pages: toRaw(data.data.pages)
      }
      postMessageDataModel.setOptions = toRaw(data.setOptions)

      if (this.projectTemplate._edit.projectType === 'multiplePage') {
        postMessageDataModel.page = this.currentScreenIndex
      }

      this.postMessageToSVG(postMessageDataModel)
    },
    /**
     * Submit data to backend
     * @param {String} action
     */
    async handleSubmitProjectTemplateDataToBackend (action) {
      // Added timeout to catch all incoming data from input onBlur events
      await new Promise(resolve => setTimeout(resolve, 100))

      let requestData = this.$utils.clone(this.projectTemplate)
      const projectId = requestData.resourceId

      delete requestData._edit
      this.isIframeDataChanged = false

      // Create Project Template Request
      if (this.projectTemplateAction === this.projectTemplateActionOptions.CREATE_PROJECT_TEMPLATE) {
        this.$utils.mainLoader(true)
        const apiRequest = () => {
          requestData = this.$utils.filterProjectDataForBackend({ project: requestData })

          // Add videowall property for video wall project
          if (requestData.templateId === 'led' && !_.isEmpty(this.$route.query.videowallTags)) {
            requestData.videowall = this.$route.query.videowallTags
          }

          this.$api.post(`${CONFIG.API_BASE_URL}/project`, requestData)
            .then(response => {
              this.isIframeDataChanged = false

              // Redirect to create template page
              this.$router.push({
                path: `/project-edit/${response.data.resourceId}/play-frame`,
                query: this.$route.query ?? undefined
              })
              this.$utils.updatePageComponentStyle({ name: 'createEditProjectPage' })
            })
            .catch((error) => {
              this.$utils.mainLoader(false)
              this.$apiError.handleUnauthorizedError(error, { originalRequest: () => { apiRequest() } })
            })
        }
        apiRequest()
      }

      // Edit Project Template request
      if (this.projectTemplateAction === this.projectTemplateActionOptions.EDIT_PROJECT_TEMPLATE) {
        const projectMediaDetails = this.$utils.clone(this.projectMediaDetails)
        let projectTemplate = this.$utils.clone(this.projectTemplate)

        // Only project template data is submitted
        if (action === 'submitTemplate') {
          const apiRequest = () => {
            this.$utils.mainLoader(true)
            projectTemplate = this.$utils.filterProjectDataForBackend({ project: projectTemplate })
            this.$api.put(`${CONFIG.API_BASE_URL}/project/${projectId}`, projectTemplate)
              .then(response => {
                // Remove unsaved data flag and update frame status
                this.isIframeDataChanged = false
                this.projectTemplateFrameStatus = 'PLAY_FRAME'
                const projectId = this.$route.params.projectId
                this.getProjectMediaDetails(projectId, true)

                // Replace route to play frame
                this.$router.replace({
                  path: `/project-edit/${this.$route.params.projectId}/play-frame`,
                  query: this.$route.query ?? undefined
                })

                this.$utils.updatePageComponentStyle({ name: 'createEditProjectPage' })
              })
              .catch((error) => {
                this.$utils.mainLoader(false)
                this.$apiError.handleUnauthorizedError(error, { originalRequest: () => { apiRequest() } })
              })
          }
          apiRequest()
        }

        // Only media data is submitted
        if (action === 'submitMedia') {
          this.removeUnsavedDataFlagInLocalStorage()
          this.$dataStore.setStore({ prevCreateProjectType: null })

          const redirectToPage = () => {
            this.isIframeDataChanged = false
            this.$dataStore.setStore({ prevPage: 'editProject' })

            // Redirect to previous page
            let redirectPath = ''
            if (this.$route.query.ledTags) {
              redirectPath = '/led'
            } else if (this.$route.query.videowallTags) {
              redirectPath = '/videowall'
            } else {
              redirectPath = '/media'
            }

            const encodedQueryString = this.$utils.encodeQueryString(this.$route.query)

            if (!_.isEmpty(encodedQueryString)) {
              redirectPath = `${redirectPath}?${encodedQueryString}`
            }

            if (redirectPath.includes('/led')) {
              const selectedLedTags = !_.isEmpty(this.$route.query.ledTags) ? [this.$route.query.ledTags] : []
              this.$filtersRepository.saveLedTags(selectedLedTags, false)
            }

            if (redirectPath.includes('/videowall')) {
              const selectedVideowallTags = !_.isEmpty(this.$route.query.videowallTags) ? [this.$route.query.videowallTags] : []
              this.$filtersRepository.saveVideowallTags(selectedVideowallTags, false)
            }

            // Try fetching media details to update the media
            // list store before redirecting to the media page.
            if (projectId) {
              this.mediaStore.fetchDetails(projectId)
                .then(({ response }) => {
                  if (response?.data?.resourceId === projectId) {
                    // Prepend the item to the media list store.
                    // This will handle adding or updating media
                    // to the list store if it already exists.
                    this.mediaListStore.prependItem(response.data)
                  }
                })
                .finally(() => {
                  // Redirect to the media page anyway whether
                  // fetching media details succeeds or fails.
                  this.$utils.mainLoader(false)
                  this.$router.push(redirectPath)
                })

              return
            }

            this.$utils.mainLoader(false)
            this.$router.push(redirectPath)
          }
          const submitMediaData = this.$currentUser.hasGroupUserLimited() || this.enableScheduleSetTarget || (!this.enableScheduleSetTarget && this.$currentUser.hasGroupUserAlert())

          if (submitMediaData) {
            const apiRequest = () => {
              this.$utils.mainLoader(true)

              // Do not send project data for edit media
              if (projectMediaDetails && projectMediaDetails.data) {
                delete projectMediaDetails.data
              }

              // Update Alert schedule
              if (projectMediaDetails && projectMediaDetails.alertSubmitBtn) {
                if (projectMediaDetails.schedules && projectMediaDetails.schedules.length) {
                  for (const schedule of projectMediaDetails.schedules) {
                    schedule.enabled = true
                  }

                  if (projectMediaDetails.alertSubmitBtn === 'reactivate') {
                    for (const schedule of projectMediaDetails.schedules) {
                      schedule.enabled = true
                    }
                  }
                }
              }

              // Do not send tags.Media tags was updated by the backend after project template updates
              if (projectMediaDetails && projectMediaDetails.tags) {
                delete projectMediaDetails.tags
              }

              this.$api.put(`${CONFIG.API_BASE_URL}/media/${projectId}`, projectMediaDetails)
                .then(response => {
                  redirectToPage()
                })
                .catch((error) => {
                  this.$apiError.handleUnauthorizedError(error, { originalRequest: () => { apiRequest() } })
                  this.$utils.mainLoader(false)
                })
            }
            apiRequest()
          } else {
            redirectToPage()
          }
        }
      }
    },
    handleProjectOrientationClick (orientation) {
      this.projectOrientation = orientation
      this.$filtersRepository.saveOrientation(orientation)

      const projectTemplate = this.$utils.clone(this.projectTemplate)
      projectTemplate.data.dir = this.projectOrientation

      this.handleProjectPreview(projectTemplate)

      this.$nextTick(() => {
        // Handle recalculation of bottom value to follow new orientation
        if (this.$refs.projectOverview) {
          this.calculateMarginAndBottomForOverviewPanel()
        }
      })
    },
    // Check for enabling/disabling Align Element context menu
    handleAlignElementsVisibility (data) {
      if (!data) {
        return
      }

      if (data.action === 'multi-select' && !data.ui?.length) {
        this.isAlignElementVisible = true
      } else if (data.action === 'exit-multi-select' || data.ui?.length) {
        // Catch data.ui if not empty as well meaning there
        // are new or other context menu to display
        this.isAlignElementVisible = false
      }
    },
    handleSVGMessage (event) {
      // Make sure event.origin is from the app to prevent accepting message out of nowhere and event.data is an object
      const sessionStage = useCognitoAuthStore().sessionStage ?? CONFIG.GLOBAL_STAGE;
      if (!sessionStage || !CONFIG.API_BASE_URLS[sessionStage]) {
        console.error('Invalid or missing stage:', sessionStage);
        return;
      }

      // Get origin depending on the stage where the user belongs
      const apiOrigin = (new URL(this.$utils.getStageAPIUrl(sessionStage))).origin;
      if ((event.origin !== apiOrigin) || (!_.isPlainObject(event.data))) return

      console.log('- Received data from SVG ', event.data)

      if (event.data?.command === 'custom:copy') {
        navigator.clipboard.writeText(event.data?.meta?.clipboard)
          .catch((_) => {
            console.warn('Cannot write into clipboard: ', event.data?.meta?.clipboard)
          })
        return
      }
      // NOTE: this is logic to pass SVG message down to
      // playback controls. It's in the beginning, since logic below might "return"
      // function control early, so might cause weird bugs
      if (this.$refs.previewPlaybackControls) {
        this.playbackControlsMessageQueue.map(this.$refs.previewPlaybackControls.handleSVGMessage)
        this.playbackControlsMessageQueue = []
        this.$refs.previewPlaybackControls.handleSVGMessage(event)
      } else {
        // Mostly for Firefox: there's no guarantee that playback controls component
        // is mountet at a time when SVG start sending messages to parent (this) component.
        // We need to catch SVG loaded event to correctly proceed with playback controls.
        this.playbackControlsMessageQueue.push(event)
      }

      const handleMessageForProjectTemplate = (eventData) => {
        if (eventData.activateClicks) {
          this.svgActivateClicks = { isActivated: true, ref: this.$utils.generateUniqueID() }

          if (this.projectTemplateFrameStatus === this.projectTemplateFrameStatusOptions.EDIT_FRAME) {
            this.projectTemplatePagesIFramePreview = this.$utils.filterMediaPreviewUrl(this.projectTemplate.edit.url)
            this.$nextTick(() => {
              this.$refs.projectFrame.$el.style.marginBottom = this.$refs.projectOverview.offsetHeight + 'px'
              this.calculateMarginAndBottomForOverviewPanel()
            })
          } else {
            this.projectTemplatePagesIFramePreview = this.$utils.filterMediaPreviewUrl(this.projectTemplate.play.url)
          }
        }

        // Event for moving items in the frame
        if (eventData.src === 'moving' || eventData.src === 'edit') {
          this.isIframeDataChanged = true
        }

        if (eventData.ui) {
          // Wait to load translations and translate names and label
          const newProjectTemplate = this.$utils.clone(this.projectTemplate)
          let waitI18nCnt = 10
          const apply = () => {
            waitI18nCnt--

            if (this.$t?.locale?.value !== null) waitI18nCnt = 0

            let shallWait = false
            if (waitI18nCnt) {
              for (const uiItem of eventData.ui) {
                if (uiItem.tname) shallWait = true

                if (uiItem.properties) {
                  for (const uiProperty of uiItem.properties) {
                    if (uiProperty.label) shallWait = true
                  }
                }
              }
            }

            if (shallWait) {
              this.$nextTick(_ => { apply() })
            } else {
              for (const uiItem of eventData.ui) {
                if (uiItem.tname) {
                  uiItem.nameNotTranslated = uiItem.tname
                  uiItem.name = this.$t(uiItem.tname) // Translate ui name
                }

                if (uiItem.properties) {
                  for (const uiProperty of uiItem.properties) {
                    if (uiProperty.label) {
                      // Translate ui property label
                      uiProperty.label = this.$t(uiProperty.label)

                      // Add label position classname for style
                      uiProperty.labelPosition = 'left-label-default'
                      if (uiProperty.type === 'string' && uiProperty.name === 'href') {
                        uiProperty.labelPosition = 'left-label-default-href'
                      } else if (uiProperty.type === 'choice' && uiProperty.name === 'frameWidth') {
                        uiProperty.labelPosition = 'left-label-default-frameWidth'
                      }
                    }

                    // Translate property name
                    if (uiProperty.tname) {
                      uiProperty.nameTranslated = this.$t(uiProperty.tname)
                    }

                    // Translate value name
                    if (uiProperty.values && _.isArray(uiProperty.values)) {
                      for (const propertyValue of uiProperty.values) {
                        if (propertyValue.tname) {
                          propertyValue.nameTranslated = this.$t(propertyValue.tname)
                        }
                      }
                    }

                    // Translate QR Code label and it's position
                    if (uiItem.style === 'qrcode' && uiProperty.name === 'qrcodedata') {
                      uiProperty.label = this.$t('url')
                      uiProperty.labelPosition = 'left-label-default-qrCode'
                    }
                  }
                }
              }
              newProjectTemplate.ui = eventData.ui
              this.projectTemplate = this.filterProjectTemplateObject(newProjectTemplate)
            }
          }
          apply()
        }

        if (eventData.hideTargets) {
          eventData.hideTargets.forEach((item) => {
            if (item.all) {
              $(`#${item.id}`).hide()
            }

            if (item.title) {
              $(`#${item.id} .title`).hide()
            }
            if (item.names) {
              // need to hide part of the interface, we show the main id, and hide the rest
              item.names.forEach((name) => {
                $(`#${item.id}_${name}`).hide()
              })
            }
          })
        }

        if (eventData.showTargets) {
          // Close automatically all color picker dropdown options
          $('.custom-color-picker-dropdown').each((indx, el) => {
            if ($(el).is(':visible')) {
              $(el).find('.el-color-dropdown__btns').find('.el-button.is-plain').click()
            }
          })

          let waitElToLoad = []
          eventData.showTargets.forEach((item) => {
            // Property Id and title
            if (!$(`#${item.id}`).length) {
              waitElToLoad.push(`#${item.id}`)
              if (item.title) waitElToLoad.push(`#${item.id} .title`)
            } else {
              $(`#${item.id}`).show()
              if (item.title) $(`#${item.id} .title`).show()
            }

            // Property with name
            if (item.names) {
              item.names.forEach((name) => { // need to show part of the interface
                if (!$(`#${item.id}_${name}`).length) {
                  waitElToLoad.push(`#${item.id}_${name}`)
                } else {
                  $(`#${item.id}_${name}`).show()
                }
              })
            }
          })

          // Process element that are not yet loaded
          if (waitElToLoad.length) {
            let waitI18nCnt = 10
            const apply = () => {
              waitI18nCnt--

              if (!waitElToLoad.length) waitI18nCnt = 0

              let shallWait = false
              if (waitI18nCnt) {
                shallWait = true
              }

              if (shallWait) {
                this.$nextTick(_ => { apply() })
              } else {
                waitElToLoad = waitElToLoad.filter((elId) => {
                  if ($(`${elId}`).length) {
                    $(`${elId}`).show()
                    return false
                  } else {
                    return true
                  }
                })
              }
            }

            apply()
          }
        }

        if (eventData.updateData || eventData.setOptions) {
          const newProjectTemplate = this.$utils.clone(this.projectTemplate)

          if (eventData.updateData) {
            eventData.updateData.forEach((item) => {
              // Format duration
              if (item.data && item.data.dur) {
                item.data.dur = this.$utils.formatDurationToMinutesAndSeconds({
                  duration: this.$utils.convertDurationToSeconds(item.data.dur)
                })
              }

              if (item.page === undefined) {
                newProjectTemplate.data[item.id] = item.data
              } else {
                newProjectTemplate.data.pages[item.page][item.id] = item.data
              }

              // If there is an item in updateData but data is null, then a delete action happened
              if (item.id && item.data === null) {
                this.setUnsavedDataFlagIntoLocalstorage({ page: 'projectTemplate' })
              }
            })
          }
          if (eventData.setOptions) newProjectTemplate.setOptions = eventData.setOptions

          this.projectTemplate = this.filterProjectTemplateObject(newProjectTemplate)
        }

        if (eventData.newPage) {
          const newProjectTemplate = this.$utils.clone(this.projectTemplate)
          const { pageIdx, ...pageData } = eventData.newPage;
          const newPage = {
            settings: {},
            tags: [],
            addApps: this.$utils.clone(newProjectTemplate.data.pages[0].addApps),
            uid: `${this.$utils.generateUniqueID()}-${newProjectTemplate.data.pages.length + 1}`,
            ...pageData
          };

          newProjectTemplate.data.pages.splice(eventData.newPage.pageIdx ?? 0, 0, newPage)
          this.currentScreenIndex = eventData.newPage.pageIdx ?? 0
          // Update project template object with the new page
          const filteredProjectTemplate = this.filterProjectTemplateObject(newProjectTemplate)
          this.projectTemplate = filteredProjectTemplate
          // Call preview to send new updated project template with new page to SVG
          this.handleProjectPreview(filteredProjectTemplate)
          // Trigger page change to update newly added page as selected
          this.handleProjectPageChange({
            action: EVENT_ACTIONS.newPageSelected,
            payload: { data: { pageNumber: eventData.newPage.pageIdx ?? 0 } }
          })
        }

        // Open Media Picker
        const firstUpdatedItem = _.isArray(eventData.updateData) && eventData.updateData.length > 0 ? eventData.updateData[0] : null
        if (_.isPlainObject(firstUpdatedItem) && isMediaTableEvent(firstUpdatedItem)) {
          // Open Media Picker when adding new media app from top controls
          if (eventData.action === 'reselect' && eventData.hideTargets.find(target => target.id.includes('media'))) {
            this.openMediaPickerDialogData = {
              menuItemId: firstUpdatedItem.id,
              time: Date.now()
            }
          }
        } else if (eventData.action === 'click' && !_.isEmpty(eventData.targetId) && isSelectableMediaEvent(eventData.mediaType, eventData.targetId)) {
          this.openMediaPickerDialogData = {
            menuItemId: eventData.targetId,
            time: Date.now()
          }
        } else if (isNewMediaAppEvent(eventData)) {
          this.openMediaPickerDialogData = {
            menuItemId: eventData?.ui[0].id ?? '',
            time: Date.now()
          }
        }

        // Set addApps menu items
        if (eventData.addApps) {
          this.addAppsTabIconVisible = false
          this.addAppsTabMenuItems = eventData.addApps

          // Add tooltip content
          this.addAppsTabMenuItems.forEach((item, indx) => {
            this.addAppsTabMenuItems[indx].tooltip = item.tname ? this.$t(item.tname) : item.name
          })
        }

        // Always select the settings tab since addApps was merged into the settings tab content
        this.selectedEditFrameTab = this.menuItemTabIds.settings

        // If media upload finished, verify all media with the same resourceId and replace preview
        if (eventData.status === 'finished' && eventData.media) {
          let countDuplicateMedia = 0
          const updatedProjectTemplate = this.$utils.clone(this.projectTemplate)

          for (const page of updatedProjectTemplate.data.pages) {
            for (const pageItemKey of Object.keys(page)) {
              if (pageItemKey && pageItemKey.startsWith('media') && (page[pageItemKey] && page[pageItemKey].resourceId) === eventData.media.resourceId) {
                page[pageItemKey].preview = eventData.media.preview
                page[pageItemKey].href = eventData.media.href
                page[pageItemKey].type = eventData.media.type
                page[pageItemKey].modifiedAt = eventData.media.modifiedAt ? eventData.media.modifiedAt : null
                page[pageItemKey].status = eventData.media.status ? eventData.media.status : null

                countDuplicateMedia += 1
              }
            }
          }

          if (countDuplicateMedia) {
            this.projectTemplate = this.filterProjectTemplateObject(updatedProjectTemplate)
            this.handleProjectPreview(this.projectTemplate)
          }
        }
      }

      // Validate iframe loaded
      if (!event.data.id || (event.data.id && event.data.id !== 'icon')) {
        if (event.data.loaded && event.data.uri) {
          $('#projectFrame').addClass('object-fit_cover_center iframe-loaded')
        } else {
          handleMessageForProjectTemplate(event.data)
        }
      }

      // Check Align Element context menu visibility
      this.handleAlignElementsVisibility(event.data)

      // Check if selection/unselection
      this.handleSelection(event.data)
    },
    handleSelection (data) {
      if (!data) return

      const selectActions = ['select', 'un-select', 'multi-select']
      if (selectActions.includes(data.action)) {
        this.$refs.projectSettings.closeColorPickers()
      }
    },
    handleSVGOnLoad () {
      const projectTemplate = this.$utils.clone(this.projectTemplate)

      this.addMessageListener()

      // Adjust Project template dimension for vertical orientation
      if (this.projectOrientation === CONST.ORIENTATION_VALUE_VERTICAL) {
        const projectFrameEl = $('#projectFrame')
        projectFrameEl.height(0)
        projectTemplate.data.dir = CONST.ORIENTATION_VALUE_VERTICAL
        setTimeout(() => { projectFrameEl.height('100%') }, 60) // Added setTimeout to remove filcker if orentation changed
      }

      // Process project preview for playFrame
      if (this.projectTemplateFrameStatus === 'PLAY_FRAME') {
        this.handleProjectPreview(projectTemplate)
      }

      // Enable project options for editFrame
      if (this.projectTemplateFrameStatus === this.projectTemplateFrameStatusOptions.EDIT_FRAME) {
        console.log('- Activating clicks')
        const postMessageData = {
          type: 'iframeOnLoad',
          activateClicks: true,
          pages: toRaw(this.projectTemplate.pages),
          style: toRaw(this.projectTemplate.style),
          tabs: {
            showAddAppsTab: (this.selectedEditFrameTab === this.menuItemTabIds.addApps),
            showSettingsTab: (this.selectedEditFrameTab === this.menuItemTabIds.settings)
          }
        }

        // Set addApps tab as default
        if (!postMessageData.tabs.showAddAppsTab && !postMessageData.tabs.showSettingsTab) {
          postMessageData.tabs.showAddAppsTab = true
        }

        this.postMessageToSVG(postMessageData)
      }
    },
    handleBackgroundClick () {
      /* Don't send a post message to SVG if there are menu items requests, there are instances that users click
        somewhere else in the page while data collection to be send into SVG is in progress */
      if (this.menuItemInProgressRequests.length > 0) {
        return
      }

      /**
       * Added timeout to catch all events from child components.
       */
      window.setTimeout(() => {
        this.postMessageToSVG({ selectedTargetId: null })
      }, 20)
    },
    postMessageToSVG (msg) {
      // Do not send postMessage in play preview
      if (this.projectTemplateFrameStatus === this.projectTemplateFrameStatusOptions.PLAY_FRAME) {
        return
      }

      const svgMessageRaw = _.cloneDeep(msg)
      console.log('- Send data to SVG ', svgMessageRaw)
      if ($('#projectFrame').length) $('#projectFrame')[0].contentWindow.postMessage(svgMessageRaw, '*')
    },
    addMessageListener () {
      if (!this.handleSVGMessageEventListenerFunction) {
        this.handleSVGMessageEventListenerFunction = this.handleSVGMessage.bind(this)
        window.addEventListener('message', this.handleSVGMessageEventListenerFunction)
      }
    },
    removeMessageListener () {
      this.postMessageToSVG({ activateClicks: false })
      this.svgActivateClicks = { isActivated: false, ref: null }

      if (this.handleSVGMessageEventListenerFunction) {
        console.log('- Removing event listener')
        window.removeEventListener('message', this.handleSVGMessageEventListenerFunction)
        this.handleSVGMessageEventListenerFunction = null
      }
    },
    /**
     * Handle Submit Button Text
     * @param {String} - projectTemplateFrameStatus
     */
    handleSubmitButtonText (projectTemplateFrameStatus) {
      if (projectTemplateFrameStatus === this.projectTemplateFrameStatusOptions.EDIT_FRAME) {
        this.submitButtonText = this.$t('label.next')
      } else {
        this.submitButtonText = this.$t('label.ok')
      }
    },
    handleEditTemplateMenuItemsTabIcon (tabIcon) {
      // If addApps or settings tab icon is selected then update template frame status
      if (tabIcon === this.menuItemTabIds.addApps || tabIcon === this.menuItemTabIds.settings) {
        this.isOverviewVisible = true
        this.projectTemplateFrameStatus = this.projectTemplateFrameStatusOptions.EDIT_FRAME

        // Replace route to edit frame
        if (this.$route.params.projectFrameAction === 'play-frame') {
          this.$router.replace({
            path: `/project-edit/${this.$route.params.projectId}/edit-frame`,
            query: this.$route.query ?? undefined
          })
        }
      }

      // If schedule is selected then set the template frame to preview and submit the data
      if (tabIcon === this.menuItemTabIds.templateSchedule) {
        this.removeMessageListener()

        if ((this.projectTemplateFrameStatus === this.projectTemplateFrameStatusOptions.EDIT_FRAME) &&
         ([this.projectTemplateActionOptions.CREATE_PROJECT_TEMPLATE, this.projectTemplateActionOptions.EDIT_PROJECT_TEMPLATE].indexOf(this.projectTemplateAction) >= 0)) {
          this.handleSubmitProjectTemplateDataToBackend('submitTemplate')
        }
      }

      const postMessageData = { selectedTargetId: null }

      // Inform SVG that the user switched tab
      if (this.addAppsTabIconVisible) {
        const newPostMessageData = this.$utils.clone(postMessageData)
        newPostMessageData.tabs = {
          showAddAppsTab: (tabIcon === this.menuItemTabIds.addApps),
          showSettingsTab: (tabIcon === this.menuItemTabIds.settings)
        }
        this.postMessageToSVG(newPostMessageData)
      }

      // Prevent sending message into the SVG while activateClicks is not yet activated
      if (this.svgActivateClicks.isActivated) {
        this.postMessageToSVG(postMessageData)
      }

      this.selectedEditFrameTab = tabIcon
    },
    handleAddAppsItemClick (app) {
      if (Object.prototype.hasOwnProperty.call(app, 'disabled') && app.disabled) {
        return
      }

      const projectTemplate = this.$utils.clone(this.projectTemplate)
      projectTemplate.data.pages[this.currentScreenIndex].addApps.style = app.value
      this.handleProjectPreview(projectTemplate)
      this.isIframeDataChanged = true
    },
    handleAddAppsOverlayClick () {
      if (this.getOverlayFromApps) {
        this.handleAddAppsItemClick(this.getOverlayFromApps)
      }

      this.isIframeDataChanged = true
    },
    handleOverviewClick () {
      this.isOverviewVisible = !this.isOverviewVisible

      if (this.$refs.projectOverview) {
        this.calculateMarginAndBottomForOverviewPanel()

        if (this.isOverviewVisible) {
          this.$refs.projectControls.scrollIntoView()
        } else {
          this.$refs.projectFrame.$el.scrollIntoView()
        }
      }
    },
    handleEditProjectNameCoverClick () {
      this.editProjectName.editing = true
      this.$refs.editProjectNameInput.focus()
    },
    applyNewProjectTemplateName () {
      if (this.projectTemplate) {
        const newProjectTemplate = this.projectTemplate
        newProjectTemplate.name = this.editProjectName.value
        this.projectTemplate = newProjectTemplate
      }

      if (this.projectMediaDetails) {
        const newProjectMediaDetails = this.projectMediaDetails
        newProjectMediaDetails.name = this.editProjectName.value
        this.projectMediaDetails = newProjectMediaDetails
      }
    },
    addProjectEventListeners () {
      const editTemplate = this.$refs.projectWrapper
      const projectFrameWrapper = this.$refs.projectFrameWrapper

      editTemplate.addEventListener('focusin', this.enableScroll)

      editTemplate.addEventListener('focusout', this.disableScroll)

      projectFrameWrapper.addEventListener('mouseleave', this.enableScroll)

      projectFrameWrapper.addEventListener('mouseenter', this.disableScroll)
    },
    removeProjectEventListeners () {
      const editTemplate = this.$refs.projectWrapper
      const projectFrameWrapper = this.$refs.projectFrameWrapper

      editTemplate.removeEventListener('focusin', this.enableScroll)

      editTemplate.removeEventListener('focusout', this.disableScroll)

      projectFrameWrapper.removeEventListener('mouseleave', this.enableScroll)

      projectFrameWrapper.removeEventListener('mouseenter', this.disableScroll)
    },
    enableScroll () {
      this.$refs.projectWrapper.style.overflowY = 'scroll'
    },
    disableScroll () {
      this.$refs.projectWrapper.style.overflowY = 'hidden'
    },
    calculateMarginAndBottomForOverviewPanel () {
      const overviewWrapper = this.$refs.projectOverview

      if (this.isOverviewVisible) {
        overviewWrapper.style.bottom = 0
        this.$refs.projectFrame.$el.style.marginBottom = overviewWrapper.offsetHeight + 'px'
      } else {
        const minimizedPanelHeight = 32
        const newBottomValue = -(overviewWrapper.offsetHeight - minimizedPanelHeight) + 'px'
        overviewWrapper.style.bottom = newBottomValue
        this.$refs.projectFrame.$el.style.marginBottom = minimizedPanelHeight + 'px'
      }
    },
    getPreviewSVG () {
      return $('#projectFrame').length ? $('#projectFrame')[0].contentWindow : null
    },
    onEnter (event) {
      if (event.target.id === 'project-name-input') {
        this.applyNewProjectTemplateName()
      }
    }
  },
  mounted () {
    this.addProjectEventListeners()
    this.isAuthenticated({
      callback: () => {
        this.$nextTick(_ => {
          this.projectOrientation = this.$filtersRepository.findOrientation() ?? CONST.ORIENTATION_VALUE_HORIZONTAL
          this.setDefaultData()
          this.handleSubmitButtonText(this.projectTemplateFrameStatus)
        })
      }
    })

    // listen to copy/cut/paste event and communicate to the SVG
    this.handlePasteEvent = (event) => {
      if (typeof document.activeElement.form === 'undefined') { // make sure there's no input focused
        const data = (event.clipboardData || window.clipboardData).getData('text')
        this.postMessageToSVG({ command: 'paste', meta: data })
        event.preventDefault()
      }
    }

    this.handleCopyEvent = (event) => {
      if (typeof document.activeElement.form === 'undefined') { // make sure there's no input focused
        this.postMessageToSVG({ command: 'copy', meta: { } })
        event.preventDefault()
      }
    }

    this.handleCutEvent = (event) => {
      if (typeof document.activeElement.form === 'undefined') { // make sure there's no input focused
        this.postMessageToSVG({ command: 'cut', meta: { } })
        event.preventDefault()
      }
    }

    window.addEventListener('paste', this.handlePasteEvent)
    window.addEventListener('copy', this.handleCopyEvent)
    window.addEventListener('cut', this.handleCutEvent)
  },
  beforeUnmount () {
    this.removeProjectEventListeners()
    this.removeMessageListener()

    // remove copy/cut/paste listener
    window.removeEventListener('paste', this.handlePasteEvent)
    window.removeEventListener('copy', this.handleCopyEvent)
    window.removeEventListener('cut', this.handleCutEvent)
  },
  beforeRouteLeave (to, from, next) {
    if (from.name === CREATE_PROJECT_PAGE && to.name === EDIT_PROJECT_PAGE) {
      next()
    } else {
      this.checkUnsavedData({ nextAction: () => next() })
    }
  }
}
